import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, Skeleton, Stack, Text } from '@mantine/core';
import BulletinSection from '@/components/common/BulletinSection';
import SellLotsTable, { type ExchangeStatusesState, type SellLotActions } from '@/components/app/sell/SellLotsTable';
import { useExchangeSession, useExchangeStatuses, useSaleOffersByBasis, useSellOfferActions } from '@/hooks/useSell';
import { isVisibleInSell } from '@/lib/sellOffer';
import { makeSellEditUrl, makeSellOfferUrl } from '@/lib/sellLinks';
import { isServiceUnavailable } from '@/api/errors';
import type { SellBasisGroup } from '@/lib/sellTree';
import type { MyBestOffer } from '@/types/bestPrice';
import type { SaleOrder } from '@/types/sell';
import { formatOrgName } from '@/lib/format';
import classes from './SellBasisCard.module.css';

interface SellBasisCardProps {
  readonly basis: SellBasisGroup;
  /** Фильтр по культуре (родительский тип); null — все культуры базиса. */
  readonly good?: string | null;
  /** Лучшая встречная цена по моему офферу (id → MyBestOffer) — для цены и «Продать» в строке. */
  readonly bestOffers?: ReadonlyMap<number, MyBestOffer>;
}

// Раздел базиса «Продать» («Бюллетень»): заголовок с зелёной линией — точка погрузки и число предложений, под ним адрес,
// затем лоты таблицами по культуре; год урожая — в строке лота, если задан. Строка ведёт на деталь предложения. Лоты базиса грузятся одним
// запросом; действия (пауза, возобновление, удаление, биржа) — в меню строки.
export default function SellBasisCard({ basis, good, bestOffers }: SellBasisCardProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useSaleOffersByBasis(basis.address, basis.addressTitle);
  const actions = useSellOfferActions();

  // Культуры базиса с видимыми лотами (ACTIVE/SUSPENDED), с учётом фильтра по культуре. Лоты группируются только по
  // культуре (решение 2026-11-04): сервер выпуска А ещё делит культуру по годам — такие группы сливаются, а год
  // показывается в строке лота. Ключ культуры вычисляется один раз: им же подписан раздел и задан ключ React.
  const cultures = useMemo(() => {
    const byCulture = new Map<string, { goods: string; lots: SaleOrder[] }>();
    for (const group of data ?? []) {
      if (good && group.parentGoodsType !== good) {
        continue;
      }
      const lots = (group.orders ?? []).filter(isVisibleInSell);
      const goods = group.parentGoodsType ?? lots[0]?.goodsType ?? '';
      const entry = byCulture.get(goods) ?? { goods, lots: [] };
      entry.lots.push(...lots);
      byCulture.set(goods, entry);
    }
    return [...byCulture.values()].filter((entry) => entry.lots.length > 0);
  }, [data, good]);

  const totalLots = cultures.reduce((sum, entry) => sum + entry.lots.length, 0);

  // Биржевые статусы подтягиваем только для биржевых лотов этого базиса (одним запросом); торговое окно —
  // общий запрос. Для внебиржевых офферов запрос не делаем.
  const exchangeIds = useMemo(
    () => cultures.flatMap((entry) => entry.lots).filter((lot) => lot.toExchange).map((lot) => lot.id),
    [cultures],
  );
  const statuses = useExchangeStatuses(exchangeIds);
  const { data: session } = useExchangeSession();
  // Без биржевых лотов запрашивать нечего. Статусов нет из-за сбоя — состояние неизвестно, действия биржевых лотов
  // скрыты. Сбой фонового обновления при уже загруженных статусах их не прячет: остаются последние известные.
  const exchangeState: ExchangeStatusesState =
    exchangeIds.length === 0 || statuses.data ? 'ready' : statuses.isError ? 'failed' : 'loading';

  const lotActions: SellLotActions = {
    onSuspend: (id) => actions.suspend.mutate(id),
    onUnsuspend: (id) => actions.unsuspend.mutate(id),
    onDelete: (id) => actions.confirmDeleteOffer(id),
    onPublishExchange: (id) => actions.publishExchange.mutate(id),
    onRemoveExchange: (id) => actions.confirmRemoveFromExchange(id),
    pendingId: actions.pendingId,
    busy: actions.anyPending,
  };

  const title = formatOrgName(basis.addressTitle) ?? basis.address;
  const unavailable = isServiceUnavailable(error) ? t('auth.errors.unavailable') : t('auth.errors.generic');

  return (
    <BulletinSection
      title={title}
      aside={totalLots > 0 ? <span className={classes.count}>{t('app.sell.lotsCount', { count: totalLots })}</span> : null}
    >
      {basis.address && basis.address !== title ? <p className={classes.address}>{basis.address}</p> : null}

      {exchangeState === 'failed' ? (
        <Group gap="md" mt="xs">
          <Text size="sm" c="loss.8">
            {t('app.sell.exchange.statusError')}
          </Text>
          <Button variant="subtle" size="compact-sm" onClick={() => statuses.refetch()} loading={statuses.isFetching}>
            {t('common.retry')}
          </Button>
        </Group>
      ) : null}

      {isError ? (
        <Group gap="md" py="sm">
          <Text c="loss.8">{unavailable}</Text>
          <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </Group>
      ) : isLoading ? (
        <Stack gap={8} mt="md">
          <Skeleton h={18} w={220} />
          <Skeleton h={36} />
          <Skeleton h={36} />
          <Skeleton h={36} />
        </Stack>
      ) : cultures.length === 0 ? (
        <Text size="sm" c="gray.7" py="sm">
          {t('app.sell.basisEmpty')}
        </Text>
      ) : (
        cultures.map(({ goods, lots }) => {
          return (
            <div key={goods} className={classes.group}>
              {/* Группа — культура; год урожая необязателен и показывается в строке лота, если задан. */}
              <h3 className={classes.groupTitle}>{goods}</h3>
              <SellLotsTable
                goods={goods}
                lots={lots}
                offerUrl={(offer) => makeSellOfferUrl(offer.id)}
                editUrl={(offer) => makeSellEditUrl(offer.id)}
                bestOffers={bestOffers}
                exchangeStatuses={statuses.data}
                exchangeState={exchangeState}
                session={session}
                actions={lotActions}
              />
            </div>
          );
        })
      )}
    </BulletinSection>
  );
}
